import { parsePhoneNumberFromString } from "libphonenumber-js";

import type { BusinessHolderFormValues } from "@/modules/dashboard/cards/components/holder/BusinessHolderForm";
import {
  offeredIdentityCountry,
  phoneSeedFor,
} from "@/modules/dashboard/cards/components/holder/holder-form.utils";
import type { PersonHolderFormValues } from "@/modules/dashboard/cards/components/holder/PersonHolderForm";
import { businessTypesFor } from "@/modules/dashboard/cards/constants/businessTypes.constants";
import { type DocumentTypeCategory, documentTypesFor } from "@/shared/data/document-types";
import { subdivisionsFor } from "@/shared/data/subdivisions";

import type { KycApplicantLinkPreview } from "../types/verification-api.types";

/**
 * Turns the backend's `draft` (the identity pre-filled from a KYC applicant, in `POST /identities` shape)
 * into the values the holder forms open with. Pure, so the mapping is tested without a DOM.
 *
 * Each value is normalized to what the form keeps: the forms reset a document type, a business type or a
 * state that is not in their lists — and a document type reset wipes its number — so a value that cannot be
 * matched is left out rather than handed over to be thrown away.
 */

type Draft = NonNullable<KycApplicantLinkPreview["draft"]>;

const normalize = (value: string): string =>
  value
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

const clean = (value: string | undefined): string | undefined => {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
};

function compact<T extends object>(record: { [K in keyof T]?: T[K] | undefined }): Partial<T> {
  return Object.fromEntries(
    Object.entries(record).filter(([, value]) => value !== undefined && value !== ""),
  ) as Partial<T>;
}

/**
 * The phone field holds the international spelling and its country (`"+52 55 1234 5678"`, `"MX"`). The draft
 * carries digits with the country code, so the country is read off the number itself.
 */
export function draftPhone(
  raw: string | undefined,
  fallbackIso3: string | undefined,
): { phone: string; phone_country: string } | undefined {
  const digits = raw?.replace(/\D/g, "");
  if (!digits) return undefined;
  const parsed = parsePhoneNumberFromString(`+${digits}`);
  if (parsed?.country) return { phone: parsed.formatInternational(), phone_country: parsed.country };
  const seed = fallbackIso3 ? phoneSeedFor(fallbackIso3) : undefined;
  return seed?.iso2 ? { phone: `+${digits}`, phone_country: seed.iso2 } : undefined;
}

/**
 * A document type the form offers for `country`. With a number but no usable type, the first one offered:
 * leaving it unset would let the form pick that same type and clear the number with it.
 */
export function draftDocumentType(
  code: string | undefined,
  country: string | undefined,
  category: DocumentTypeCategory,
  hasNumber: boolean,
): string | undefined {
  if (!country) return undefined;
  const offered = documentTypesFor(country, category);
  if (code && (offered as readonly string[]).includes(code)) return code;
  return hasNumber ? offered[0] : undefined;
}

/** The form's business type that `raw` spells ("S.A. DE C.V." → "S.A. de C.V."), or nothing. */
export function draftBusinessType(raw: string | undefined, country: string | undefined): string | undefined {
  if (!raw || !country) return undefined;
  const wanted = normalize(raw);
  return businessTypesFor(country).find(({ value }) => normalize(value) === wanted)?.value;
}

/**
 * The catalog name of a state the applicant spelled its own way ("CDMX", "ciudad de mexico"). A country
 * without a catalog keeps the text, since its field is free text.
 */
export function draftState(raw: string | undefined, country: string | undefined): string | undefined {
  const value = clean(raw);
  if (!value) return undefined;
  const rows = subdivisionsFor(country);
  if (rows.length === 0) return value;
  const wanted = normalize(value);
  const match = rows.find((row) =>
    [row.name, row.displayName, row.code, row.code.split("-")[1], ...(row.aliases ?? [])]
      .filter((term): term is string => !!term)
      .some((term) => normalize(term) === wanted),
  );
  return match?.name;
}

export function businessFormValuesFromDraft(draft: Draft): Partial<BusinessHolderFormValues> {
  const { identity, address } = draft;
  const country = offeredIdentityCountry(identity.country);
  const ownerCountry = offeredIdentityCountry(identity.owner_country_code) ?? country;
  const addressCountry = offeredIdentityCountry(address.country) ?? country;
  return compact<BusinessHolderFormValues>({
    legal_name: clean(identity.legal_name),
    business_type: draftBusinessType(identity.business_type, country),
    country,
    identification_type: draftDocumentType(
      identity.identification_type,
      country,
      "fiscal",
      !!clean(identity.identification_number),
    ),
    identification_number: clean(identity.identification_number),
    incorporation_date: clean(identity.incorporation_date),
    email: clean(identity.email),
    ...draftPhone(identity.phone, country),
    owner_name: clean(identity.owner_name),
    owner_country_code: ownerCountry,
    owner_id_type: draftDocumentType(
      identity.owner_id_type,
      ownerCountry,
      "personal",
      !!clean(identity.owner_id_number),
    ),
    owner_id_number: clean(identity.owner_id_number),
    owner_address_line1: clean(identity.owner_address_line1),
    owner_city: clean(identity.owner_city),
    owner_state: clean(identity.owner_state),
    owner_postal_code: clean(identity.owner_postal_code),
    address_line_1: clean(address.address_line_1),
    address_line_2: clean(address.address_line_2),
    city: clean(address.city),
    state: draftState(address.state, addressCountry),
    postal_code: clean(address.postal_code),
  });
}

export function personFormValuesFromDraft(draft: Draft): Partial<PersonHolderFormValues> {
  const { identity, address } = draft;
  const idCountry = offeredIdentityCountry(identity.country);
  const addressCountry = offeredIdentityCountry(address.country) ?? idCountry;
  const gender = identity.gender === "M" || identity.gender === "F" ? identity.gender : undefined;
  return compact<PersonHolderFormValues>({
    email: clean(identity.email),
    first_name: clean(identity.first_name),
    last_name: clean(identity.last_name),
    birthdate: clean(identity.birthdate),
    gender,
    id_country: idCountry,
    nationality: offeredIdentityCountry(identity.nationality) ?? idCountry,
    identification_type: draftDocumentType(
      identity.identification_type,
      idCountry,
      "personal",
      !!clean(identity.identification_number),
    ),
    identification_number: clean(identity.identification_number),
    ...draftPhone(identity.phone, idCountry),
    address_line_1: clean(address.address_line_1),
    address_line_2: clean(address.address_line_2),
    city: clean(address.city),
    state: draftState(address.state, addressCountry),
    country: addressCountry,
    postal_code: clean(address.postal_code),
  });
}
